import { DefaultOptionType } from 'antd/lib/select';

export const EMAIL_NOTIFICATION_TYPE = {
  MARKET_INTELLIGENCE_DIGEST: 1,
  ONLINE_REVIEWS_DIGEST: 2,
  MARKET_RATE_COMPARISON_WARNING: 3,
  COMPETITOR_INCENTIVE_UPDATES: 4,
  REMINDER_TO_UPDATE_PRICING: 5,
  SALES_BATTLECARD_DIGEST: 6,
  MARKET_RATE_COMPARISON_UPDATES: 7,
  COMMUNITY_REVIEW_ALERTS: 8,
  COMPETITOR_REVIEW_ALERTS: 9,
};

export const DEFAULT_MARKET_RATE_PERCENT = 10;

export const CMI_DIGEST_FREQUENCY = {
  WEEKLY: 'weekly',
  BI_WEEKLY: 'bi_weekly',
};

export const CMI_DIGEST_FREQUENCY_OPTIONS = [
  { label: 'Weekly', value: CMI_DIGEST_FREQUENCY.WEEKLY },
  { label: 'Bi-Weekly', value: CMI_DIGEST_FREQUENCY.BI_WEEKLY },
];

export const CMI_DIGEST_ADMIN_STATUS_OPTIONS = [
  { label: 'Any Status', value: null },
  { label: 'On', value: 'true' },
  { label: 'Off', value: 'false' },
];

export const CMI_DIGEST = {
  icon: {
    backgroundColor: 'var(--hot-pink)',
    backgroundImage: 'linear-gradient(315deg, var(--competitor-purple), #f05fff 50%, var(--flamingo-pink) 0%)',
    url: '/icons/email.svg',
  },
  section: 'Email Notifications',
  title: 'CMI Personal Digest',
  description:
    'Choose if an AI based digest email is sent summarizing the most impactful things happening across your communities each week.',
  url: '/preferences/cmi-digest',
};

export const MARKET_INTELLIGENCE_DIGEST = {
  type: EMAIL_NOTIFICATION_TYPE.MARKET_INTELLIGENCE_DIGEST,
  field: 'market_intelligence_digest_notifications',
  statusElements: {
    ON: {
      label: 'On',
      value: 1,
    },
    OFF: {
      label: 'Off',
      value: 0,
    },
  },
  icon: {
    backgroundColor: '#22cfd1',
    url: '/icons/newspaper.svg',
  },
  section: 'Email Notifications',
  title: 'Market Intelligence Digest',
  description: 'Choose if a digest email is sent summarizing market rates and pricing changes after each mystery shop.',
  url: '/preferences/market-intelligence-digest',
};

export const ONLINE_REVIEWS_DIGEST = {
  type: EMAIL_NOTIFICATION_TYPE.ONLINE_REVIEWS_DIGEST,
  field: 'online_reviews_digest_notifications',
  statusElements: {
    QUARTERLY: {
      label: 'Quarterly',
      value: 2,
    },
    MONTHLY: {
      label: 'Monthly',
      value: 1,
    },
    OFF: {
      label: 'Off',
      value: 0,
    },
  },
  icon: {
    backgroundColor: '#0ab776',
    url: '/icons/sentiment-satisfied.svg',
  },
  section: 'Email Notifications',
  title: 'Online Reviews Digest',
  description: ' Choose if a monthly digest email is sent summarizing online review performance.',
  url: '/preferences/online-reviews-digest',
};

export const SALES_BATTLECARD_DIGEST = {
  type: EMAIL_NOTIFICATION_TYPE.SALES_BATTLECARD_DIGEST,
  field: 'sales_battlecard_digest_notifications',
  statusElements: {
    ON: {
      label: 'On',
      value: 1,
    },
    OFF: {
      label: 'Off',
      value: 0,
    },
  },
  icon: {
    backgroundColor: '#0ab776',
    url: '/icons/sales-battlecard.svg',
  },
  section: 'Email Notifications',
  title: 'Sales Battlecard Digest',
  description: 'Choose if a digest email is sent summarizing sales battlecards.',
  url: '/preferences/sales-battlecard-digest',
};

export const COMPETITOR_INCENTIVE_UPDATES = {
  type: EMAIL_NOTIFICATION_TYPE.COMPETITOR_INCENTIVE_UPDATES,
  field: 'incentive_email_notifications',
  statusElements: {
    ON: {
      label: 'On',
      value: 1,
    },
    OFF: {
      label: 'Off',
      value: 0,
    },
  },
  icon: {
    backgroundColor: '#007aff',
    url: '/icons/local-offer.svg',
  },
  section: 'Email Notifications',
  title: 'Competitor Incentive Updates',
  description: 'Choose if an email is sent when a competitor adds new incentives.',
  url: '/preferences/competitor-incentive-updates',
};

export const MARKET_RATE_COMPARISON_UPDATES = {
  type: EMAIL_NOTIFICATION_TYPE.MARKET_RATE_COMPARISON_UPDATES,
  field: 'market_rate_comparison_updates',
  statusElements: {
    ON: {
      label: 'On',
      value: 1,
    },
    OFF: {
      label: 'Off',
      value: 0,
    },
  },
  statusFilterElements: {
    OVER: {
      label: "Over competitors' rate",
      value: 2,
    },
    UNDER: {
      label: "Under competitors' rate",
      value: 1,
    },
    OFF: {
      label: 'Off',
      value: 0,
    },
  },
  icon: {
    backgroundColor: '#5f5fff',
    url: '/icons/price-change.svg',
  },
  section: 'Email Notifications',
  title: 'Market Rate Comparison Updates',
  description: "Choose if an email is sent if your market rate is significantly over or under the competitors' rates.",
  recipientsModalDescription:
    "Choose if an email is sent if your market rate is significantly over or under the competitors' rates. We compare your community's starting-at rate against each competitor's starting-at rate per room category (Shared, Studio, One Bedroom, Two Bedroom, Three Bedroom) and care type.",
  url: '/preferences/market-rate-comparison-updates',
};

export const REMINDER_TO_UPDATE_PRICING_STATUS = {
  type: EMAIL_NOTIFICATION_TYPE.REMINDER_TO_UPDATE_PRICING,
  statusElements: {
    MONTHLY: {
      label: 'Monthly',
      value: 1,
    },
    EVERY_2_MONTHS: {
      label: 'Every 2 months',
      value: 2,
    },
    QUARTERLY: {
      label: 'Quarterly',
      value: 3,
    },
    EVERY_6_months: {
      label: 'Every 6 months',
      value: 4,
    },
    OFF: {
      label: 'Off',
      value: 5,
    },
  },
  icon: {
    backgroundColor: '#ff5b98',
    url: '/icons/rule.svg',
  },
  section: 'Email Notifications',
  title: 'Reminder to Update Pricing',
  description: 'Choose if an email is sent reminding that pricing has not been updated in a while.',
  url: '/preferences/reminder-to-update-pricing',
};

export const COMMUNITY_REVIEW_ALERTS = {
  type: EMAIL_NOTIFICATION_TYPE.COMMUNITY_REVIEW_ALERTS,
  statusElements: {
    ON: {
      label: 'On',
      value: 1,
    },
    OFF: {
      label: 'Off',
      value: 0,
    },
  },
  statusFilterElements: {
    ALL_ON: {
      label: 'All On',
      value: 1,
    },
    ALL_OFF: {
      label: 'All Off',
      value: 2,
    },
    COMMUNITY_ON: {
      label: 'Community Alerts On',
      value: 3,
    },
    COMMUNITY_OFF: {
      label: 'Community Alerts Off',
      value: 4,
    },
    COMPETITOR_ON: {
      label: 'Competitor Alerts On',
      value: 5,
    },
    COMPETITOR_OFF: {
      label: 'Competitor Alerts Off',
      value: 6,
    },
  },
  icon: {
    backgroundColor: '#fa7550',
    url: '/icons/review.svg',
  },
  section: 'Email Notifications',
  title: 'Community Review Alerts',
  description: 'Get alerted when communities or competitors receive reviews matching your alert criteria.',
  recipientsModalDescription: '',
  url: '/preferences/community-review-alerts',
};

export const SPREADSHEET_EXPORT = {
  icon: {
    backgroundColor: '#22cfd1',
    url: '/icons/spreadsheet.svg',
  },
  section: 'Spreadsheet Export',
  title: 'Spreadsheet Export',
  description: 'Customize how your data is exported to spreadsheets for easier sharing, analysis, and presentation.',
  url: '/preferences/spreadsheet-export',
};

export const COMMUNITY_LABEL_COLOR = {
  NEUTRAL: 0,
  POSITIVE: 1,
};

export const REVIEW_ALERT_CATEGORIES = [
  { id: 1, label: 'Staff' },
  { id: 2, label: 'Room' },
  { id: 3, label: 'Care' },
  { id: 4, label: 'Cleanliness' },
  { id: 5, label: 'Service' },
  { id: 6, label: 'Dining' },
] as const;

export const SETTINGS_PAGE_INFO: { [key: string]: any } = {
  MARKET_INTELLIGENCE_DIGEST: { ...MARKET_INTELLIGENCE_DIGEST },
  ONLINE_REVIEWS_DIGEST: { ...ONLINE_REVIEWS_DIGEST },
  SALES_BATTLECARD_DIGEST: { ...SALES_BATTLECARD_DIGEST },
  MARKET_RATE_COMPARISON: { ...MARKET_RATE_COMPARISON_UPDATES },
  REMINDER_TO_UPDATE_PRICING: { ...REMINDER_TO_UPDATE_PRICING_STATUS },
  COMMUNITY_REVIEW_ALERTS: { ...COMMUNITY_REVIEW_ALERTS },
  COMPETITOR_INCENTIVE: { ...COMPETITOR_INCENTIVE_UPDATES },
  SPREADSHEET_EXPORT: { ...SPREADSHEET_EXPORT },
};

export const COMMUNITY_SETTINGS_PAGE_TYPE = {
  MARKET_INTELLIGENCE_DIGEST: 'MARKET_INTELLIGENCE_DIGEST',
  ONLINE_REVIEWS_DIGEST: 'ONLINE_REVIEWS_DIGEST',
  SALES_BATTLECARD_DIGEST: 'SALES_BATTLECARD_DIGEST',
  MARKET_RATE_COMPARISON: 'MARKET_RATE_COMPARISON',
  REMINDER_TO_UPDATE_PRICING: 'REMINDER_TO_UPDATE_PRICING',
  COMPETITOR_INCENTIVE: 'COMPETITOR_INCENTIVE',
  COMMUNITY_REVIEW_ALERTS: 'COMMUNITY_REVIEW_ALERTS',
  SPREADSHEET_EXPORT: 'SPREADSHEET_EXPORT',
};

export const COMMUNITY_SETTINGS_LIST_PAGE_SIZE = 10;
export const SETTINGS_EXTRA_COLUMN_WIDTH = 330;

export const SPREADSHEET_EXPORT_COLOR_MODE = {
  PREDEFINED_COLORS: 1,
  CUSTOM_COLOR: 2,
};

export const SPREADSHEET_PREDEFINED_COLORS = {
  PURPLE: '#5858F0',
  DARK_BLUE: '#1F4E79',
  BLUE: '#3A9DF8',
  CYAN: '#50C0D0',
  GREEN: '#2FB67B',
  YELLOW: '#D6A51B',
  ORANGE: '#F26B4A',
  RED: '#E33A3F',
  PINK: '#FF6580',
  GRAY: '#8A8D93',
};

export const SHOW_RECIPIENTS_OPTIONS: DefaultOptionType[] = [
  { label: 'All', value: 0 },
  { label: 'Selected', value: 1 },
];
